import type { ObjectFitType } from "@v1/types/object-fit";
import type { AssetLoadingType } from "replo-runtime/shared/asset-loading";
import type { Context } from "replo-runtime/store/ReploVariable";
import type { ReploMixedStyleValue } from "replo-runtime/store/utils/mixed-values";

import * as React from "react";

import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import DynamicDataButton from "@v1/components/common/designSystem/DynamicDataButton";
import SelectionIndicator from "@v1/components/common/designSystem/SelectionIndicator";
import Popover from "@v1/components/designSystem/popover/Popover";
import { DeprecatedTextarea } from "@v1/components/designSystem/textarea/Textarea";
import AssetPicker from "@v1/components/editor/AssetPicker";
import DynamicDataValueIndicator from "@v1/components/editor/page/element-editor/components/extras/DynamicDataValueIndicator";
import useApplyComponentAction from "@v1/hooks/useApplyComponentAction";
import {
  selectDraftComponentIds,
  selectImageAltText,
} from "@v1/reducers/core-reducer";
import {
  resetEditorBrokenMediaComponentId,
  selectEditorBrokenMediaComponentIds,
} from "@v1/reducers/editor-media-reducer";
import { useEditorDispatch, useEditorSelector } from "@v1/store/store";
import { useAreModalsOpen } from "@v2/store/modalStore";
import { Info } from "lucide-react";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { isDynamicDataValue } from "replo-runtime/shared/utils/dynamic-data";
import { evaluateVariableAsString } from "replo-runtime/store/ReploVariable";
import { isMixedStyleValue } from "replo-runtime/store/utils/mixed-values";
import { useDebouncedCallback } from "replo-utils/react/use-debounced-callback";

import { DynamicDataSelector } from "../../../DynamicDataSelector";
import ControlGroup from "../extras/ControlGroup";
import ModifierLabel from "../extras/ModifierLabel";
import InlineAssetSelector from "./InlineAssetSelector";

type ImageSourceSelectorProps = {
  src?: string | ReploMixedStyleValue;
  objectFitValue?: ObjectFitType | ReploMixedStyleValue;
  assetLoadingValue?: AssetLoadingType | ReploMixedStyleValue;
  objectPositionValues?: {
    x?: string | ReploMixedStyleValue;
    y?: string | ReploMixedStyleValue;
  };
  onChangeImageSource(value: string): void;
  onChangeObjectFit?(value: string): void;
  onChangeAssetLoading?(value: AssetLoadingType): void;
  onChangeObjectPositionX?(value: string): void;
  onChangeObjectPositionY?(value: string): void;
  onRemove(): void;
  componentContext?: Context;
  allowsDynamicData?: boolean;
  componentId?: string;
  openPopoverWhenEmpty?: boolean;
  generateAltText?: (imageUrl: string) => void;
  isGeneratingAltText?: boolean;
  size: "sm" | "base";
  offset?: number;
};

const ImageSourceSelector = ({
  src: originalSource,
  objectFitValue,
  assetLoadingValue,
  objectPositionValues,
  onChangeImageSource,
  onChangeObjectFit,
  onChangeAssetLoading,
  onChangeObjectPositionX,
  onChangeObjectPositionY,
  onRemove,
  componentContext,
  allowsDynamicData = true,
  componentId,
  openPopoverWhenEmpty = false,
  size,
  offset,
}: ImageSourceSelectorProps) => {
  const dispatch = useEditorDispatch();
  const editorBrokenMediaComponentIds = useEditorSelector(
    selectEditorBrokenMediaComponentIds,
  );
  const isAssetDynamicData = !isMixedStyleValue(originalSource)
    ? isDynamicDataValue(originalSource)
    : false;
  const evaluatedSource =
    originalSource && !isMixedStyleValue(originalSource)
      ? evaluateVariableAsString(originalSource, componentContext)
      : null;
  const resolvedSource =
    isAssetDynamicData && componentContext && evaluatedSource
      ? evaluatedSource
      : originalSource;
  const draftComponentIds = useEditorSelector(selectDraftComponentIds);
  const firstDraftComponentId = draftComponentIds[0];

  const isImageBroken = draftComponentIds.some((id) => {
    return editorBrokenMediaComponentIds.includes(id);
  });
  const isPopoverVisible = !resolvedSource || isImageBroken;

  const [isVisible, setVisible] = useOverridableState(
    openPopoverWhenEmpty ? isPopoverVisible : false,
    undefined,
    firstDraftComponentId ?? undefined,
  );
  const areModalsOpen = useAreModalsOpen();
  const applyComponentAction = useApplyComponentAction();

  const _handleAltTextChange = React.useCallback(
    (value: string) => {
      applyComponentAction({
        type: "applyCompositeAction",
        value: [
          {
            type: "setStyles",
            value: { __imageAltText: value },
          },
        ],
      });
    },
    [applyComponentAction],
  );

  const initialAltTextValue = useEditorSelector(selectImageAltText);

  const [altTextValue, setAltTextValue] = useOverridableState(
    isMixedStyleValue(initialAltTextValue)
      ? "Mixed"
      : initialAltTextValue ?? "",
    useDebouncedCallback(_handleAltTextChange, 300),
  );

  const handleRemove = () => {
    onRemove();
  };

  const onClickSelectAsset = () => {
    // Note (Noah, 2023-12-07): Regardless of whether we allow changing
    // object-fit etc, always set the popover visible instead of going straight
    // to the asset modal because we always support updating the alt text, for
    // which we need to open the popover
    setVisible(true);
  };

  const handleInputChange = (value: string, altText?: string | null) => {
    onChangeImageSource(value);
    setAltTextValue(altText ?? "");
    if (isImageBroken && firstDraftComponentId) {
      dispatch(resetEditorBrokenMediaComponentId(firstDraftComponentId));
    }
  };

  return (
    <Popover.Root
      isOpen={isVisible}
      onOpenChange={setVisible}
      // Note (Noah, 2022-04-23, REPL-1738): Can't ignore outside interactions
      // here because if we do, that makes the asset library modal not scrollable
      shouldIgnoreOutsideInteractions={false}
    >
      <Popover.Anchor className="w-full">
        {typeof originalSource === "string" && isAssetDynamicData ? (
          <DynamicDataValueIndicator
            type="image"
            templateValue={originalSource}
            onClick={onClickSelectAsset}
            onRemove={handleRemove}
            componentId={componentId}
          />
        ) : (
          <InlineAssetSelector
            emptyTitle="Select Image"
            inputPlaceholder="Image URL"
            onClickSelectAsset={onClickSelectAsset}
            onRemoveAsset={handleRemove}
            allowRemoveAsset
            shouldOpenLibraryPopover={false}
            asset={
              isMixedStyleValue(originalSource)
                ? originalSource
                : {
                    type: "image",
                    // TODO (Noah, 2023-05-23): Added this "as string" since we were assuming
                    // it before we switched the return type of evaluateVariable to unknown,
                    // but need to investigate more since the undefined case should be handled
                    // somewhere above
                    src: resolvedSource as string,
                  }
            }
            allowsDynamicData={allowsDynamicData}
            isImageOnly
            onChange={handleInputChange}
            size={size}
          />
        )}
      </Popover.Anchor>
      {isVisible && (
        <Popover.Content
          title="Image"
          shouldPreventDefaultOnInteractOutside={areModalsOpen}
          sideOffset={offset}
        >
          <AssetPicker
            allowsSettingDynamicData={allowsDynamicData}
            objectFitValue={objectFitValue}
            assetLoadingValue={assetLoadingValue}
            objectPositionValue={objectPositionValues}
            assetType="image"
            emptyTitle="No Image Selected"
            selectedStateButtonLabel="Select Image"
            emptyStateButtonLabel="Change Image"
            url={resolvedSource}
            onChangeObjectFit={onChangeObjectFit}
            onChangeAssetLoading={onChangeAssetLoading}
            onChangeObjectPositionX={onChangeObjectPositionX}
            onChangeObjectPositionY={onChangeObjectPositionY}
            onChangeSource={handleInputChange}
            componentId={componentId}
            isUrlDynamicData={isAssetDynamicData}
            templateValue={originalSource}
            onClickRemoveDynamicData={onRemove}
            altTextComponent={
              <ControlGroup
                className={twMerge(
                  "flex",
                  isDynamicDataValue(altTextValue)
                    ? "items-center"
                    : "items-start",
                )}
              >
                <ModifierLabel label="Alt Text" />
                {(() => {
                  if (isDynamicDataValue(altTextValue)) {
                    return (
                      <DynamicDataSelector
                        trigger={
                          <DynamicDataValueIndicator
                            type="text"
                            templateValue={altTextValue ?? ""}
                            onRemove={() => setAltTextValue("")}
                            componentId={componentId}
                          />
                        }
                        targetType="text"
                        onChange={(newValue) => {
                          setAltTextValue(newValue);
                        }}
                      />
                    );
                  }

                  if (componentContext?.isInsideProductImageCarousel) {
                    return (
                      <div className="flex flex-grow items-center gap-1">
                        <SelectionIndicator
                          title=""
                          placeholder="From Product Image"
                          disabled
                          endEnhancer={
                            <Tooltip
                              triggerAsChild
                              content="To change this image's alt text, edit in Shopify Admin"
                            >
                              <div className="flex items-center justify-center">
                                <Info className="text-muted" size={12} />
                              </div>
                            </Tooltip>
                          }
                        />
                      </div>
                    );
                  }

                  return (
                    <div className="flex flex-1 gap-1">
                      <div className="relative flex-1 items-center">
                        <div className="absolute bottom-0 left-0 right-0 top-0">
                          <DeprecatedTextarea
                            value={altTextValue}
                            onChange={setAltTextValue}
                            layoutClassName="h-14"
                            placeholder="Description"
                            isDisabled={
                              !resolvedSource || draftComponentIds.length > 1
                            }
                            size="sm"
                          />
                        </div>
                      </div>
                      <div className="flex min-h-16 flex-col gap-2">
                        {allowsDynamicData && (
                          <div className="shrink-0">
                            <DynamicDataSelector
                              trigger={<DynamicDataButton />}
                              targetType="text"
                              onChange={(newValue) => {
                                setAltTextValue(newValue);
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </ControlGroup>
            }
          />
        </Popover.Content>
      )}
    </Popover.Root>
  );
};

export default ImageSourceSelector;
